/**
 * AuthGuard Component
 * Protects routes and validates vendor role
 */

import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/auth';
import { initializeAuth } from '@/lib/auth';
import { vendorApi } from '@/lib/api';
import { logger } from '@/lib/logger';

interface AuthGuardProps {
  children: React.ReactNode;
}

export default function AuthGuard({ children }: AuthGuardProps) {
  const location = useLocation();
  const { isAuthenticated, user, setAuth, clearAuth, setVendor } = useAuthStore();

  useEffect(() => {
    // Initialize auth from localStorage ONLY if not already authenticated
    if (isAuthenticated) return;
    
    const { isAuthenticated: authed, user: storedUser } = initializeAuth();

    if (authed && storedUser) {
      // Validate vendor role
      if (storedUser.role !== 'vendor') {
        logger.error('Forbidden: Non-vendor user attempted access', new Error('Forbidden'), {
          userId: storedUser.id,
          role: storedUser.role,
          path: location.pathname,
        });
        clearAuth();
        return;
      }

      setAuth(storedUser);

      // Fetch vendor profile
      vendorApi
        .getProfile()
        .then((vendor) => {
          setVendor(vendor);
          logger.info('Vendor profile loaded in AuthGuard', { vendorId: vendor.id });
        })
        .catch((error) => {
          logger.error('Failed to load vendor profile in AuthGuard', error);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run ONLY ONCE on mount

  // Check authentication
  if (!isAuthenticated) {
    logger.warn('Unauthenticated access attempt', { path: location.pathname });
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check vendor role from store (not localStorage)
  if (user && user.role !== 'vendor') {
    logger.error('Forbidden: Non-vendor user attempted access', new Error('Forbidden'), {
      userId: user.id,
      role: user.role,
      path: location.pathname,
    });
    clearAuth();
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}









